// 사진 화면용 순수 규칙 — 현장 상세 「최근 사진」 고르기와 사진이 안 뜰 때의 안내 문구.

import type { ProjectPhoto, ProjectStatus } from "./types";

export const RECENT_PHOTO_LIMIT = 8;

// 최근 올린 순 — 기록 날짜 최근 먼저, 같은 날은 늦게 올린 것 먼저 · 전부 내림차순(Supabase 는 이 열 순서대로 order).
export const RECENT_PHOTO_ORDER = ["log_date", "created_at", "id"] as const;

type RecentKey = Pick<ProjectPhoto, (typeof RECENT_PHOTO_ORDER)[number]>;

export const compareRecentPhotos = (a: RecentKey, b: RecentKey): number => {
  for (const key of RECENT_PHOTO_ORDER) {
    const diff = b[key].localeCompare(a[key]);
    if (diff !== 0) return diff;
  }
  return 0;
};

export const recentPhotosOf = <T extends RecentKey>(photos: readonly T[], limit = RECENT_PHOTO_LIMIT): T[] =>
  [...photos].sort(compareRecentPhotos).slice(0, Math.max(0, limit));

// 착공 전(시공대기) 현장은 올린 사진이 없으면 「최근 사진」 칸을 숨긴다.
export const showsRecentPhotos = (status: ProjectStatus, total: number): boolean => status !== "SCHEDULED" || total > 0;

// 내부 사진 바이트 경로 — src/app/app/projects/[id]/photos/[photoId]/route.ts.
export const projectPhotoPath = (projectId: string, photoId: string): string =>
  `/app/projects/${encodeURIComponent(projectId)}/photos/${encodeURIComponent(photoId)}`;

// 「날짜별로 보기」 — 따로 모아 보는 화면이 없어 그날의 오늘 기록 화면으로 보낸다.
export const projectDayPath = (projectId: string, date: string): string =>
  `/app/projects/${encodeURIComponent(projectId)}/today?date=${encodeURIComponent(date)}`;

// member = 내부(/app, 세션 쿠키) · customer = 고객 링크(/c, 토큰+입장 쿠키 — 401 을 쓰지 않는다).
export type PhotoAudience = "member" | "customer";

export type PhotoFailKind = "login" | "link" | "missing" | "retry" | "unknown";

export interface PhotoFailure {
  kind: PhotoFailKind;
  text: string;
}

export const PHOTO_FAIL_TEXT: Record<PhotoFailKind, string> = {
  login: "로그인이 끝났습니다",
  link: "링크를 다시 열어 주세요",
  missing: "사진이 없습니다",
  retry: "잠시 후 다시 봐 주세요",
  unknown: "사진을 불러오지 못했습니다",
};

const failure = (kind: PhotoFailKind): PhotoFailure => ({ kind, text: PHOTO_FAIL_TEXT[kind] });

// status 는 다시 물어본 응답 코드(null = 네트워크 실패) · missingBytes 는 행은 있고 바이트만 없다는 표시 헤더.
export const photoFailureOf = (
  status: number | null,
  audience: PhotoAudience,
  missingBytes = false
): PhotoFailure => {
  if (status === null || status === 429 || status === 503) return failure("retry");
  if (status === 401) return failure(audience === "member" ? "login" : "link");
  if (status === 404) {
    // 고객 경로의 404 는 죽은 링크·쿠키 없음·없는 사진을 일부러 가르지 않는다 — 바이트만 없을 때만 「없음」.
    if (audience === "customer" && !missingBytes) return failure("link");
    return failure("missing");
  }
  if (status >= 500) return failure("retry");
  return failure("unknown");
};
